import React, { useState, useEffect } from 'react';
import { getUserProfile, updateProfile } from '../../api/userService';
import CompanionStudio from '../../components/companion/CompanionStudio';
import { getUserStats } from '../../api/userService';
import { setBirthDate } from '../../api/authService';
import Card, { CardHeader, CardContent, CardFooter } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Loader from '../../components/ui/Loader';
import { useAuth } from '../../hooks/useAuth';
import './Profile.css';

const Profile = () => {
  const { logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState({});
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  // R35 : date de naissance (age-gating 18+) — settable une seule fois
  const { user, refreshUser } = useAuth();
  const [birthDateInput, setBirthDateInput] = useState('');
  const [birthDateMsg, setBirthDateMsg] = useState(null);

  const birthDateSet = !!user?.birthDate;

  const handleSetBirthDate = async () => {
    if (!birthDateInput) return;
    try {
      setBirthDateMsg(null);
      const r = await setBirthDate(birthDateInput);
      setBirthDateMsg(r.adult
        ? '✅ Enregistrée — accès 18+ débloqué'
        : '✅ Enregistrée — les fonctionnalités 18+ resteront verrouillées jusqu’à tes 18 ans');
      if (refreshUser) await refreshUser();
    } catch (e) {
      setBirthDateMsg('❌ ' + (e.message || 'Erreur'));
    }
  };

  // Charger les données du profil et les statistiques
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // Charger le profil
        const profileResponse = await getUserProfile();
        setProfile(profileResponse);
        setEditedProfile({
          username: profileResponse.username,
          email: profileResponse.email,
          bio: profileResponse.bio || '',
          avatarUrl: profileResponse.avatarUrl || '',
          preferences: {
            theme: profileResponse.preferences?.theme || 'dark',
            notifications: profileResponse.preferences?.notifications || true,
            soundEffects: profileResponse.preferences?.soundEffects || true,
            music: profileResponse.preferences?.music || true
          }
        });
        
        // Charger les statistiques
        const statsResponse = await getUserStats();
        setStats(statsResponse.stats);
        
        setError(null);
      } catch (err) {
        setError('Erreur lors du chargement des données. Veuillez réessayer.');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, []);

  // Gérer les changements dans le formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      // Pour les champs imbriqués comme preferences.theme
      const [parent, child] = name.split('.');
      setEditedProfile(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setEditedProfile(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Gérer les changements dans les toggles (checkbox)
  const handleToggleChange = (e) => {
    const { name, checked } = e.target;
    const [parent, child] = name.split('.');
    
    setEditedProfile(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [child]: checked
      }
    }));
  };

  // Sauvegarder les modifications
  const handleSaveProfile = async () => {
    try {
      setIsSaving(true);
      
      // Envoyer les données modifiées
      await updateProfile(editedProfile);
      
      // Mettre à jour le profil affiché
      setProfile({
        ...profile,
        ...editedProfile
      });
      
      setIsEditing(false);
      setError(null);
    } catch (err) {
      setError('Erreur lors de la mise à jour du profil. Veuillez réessayer.');
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="profile-loading">
        <Loader text="Chargement du profil..." />
      </div>
    );
  }

  return (
    <div className="profile-container">
      <h1>Profil</h1>
      
      {error && <div className="profile-error">{error}</div>}
      
      <div className="profile-content">
        <div className="profile-section">
          <Card>
            <CardHeader>
              <h2>Informations personnelles</h2>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <div className="profile-form">
                  <div className="form-group">
                    <label htmlFor="username">Nom d'utilisateur</label>
                    <input
                      type="text"
                      id="username"
                      name="username"
                      value={editedProfile.username}
                      onChange={handleInputChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={editedProfile.email}
                      onChange={handleInputChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="bio">Bio</label>
                    <textarea
                      id="bio"
                      name="bio"
                      value={editedProfile.bio}
                      onChange={handleInputChange}
                      rows="3"
                    ></textarea>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="avatarUrl">URL de l'avatar</label>
                    <input
                      type="text"
                      id="avatarUrl"
                      name="avatarUrl"
                      value={editedProfile.avatarUrl}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              ) : (
                <div className="profile-info">
                  <div className="profile-avatar">
                    <img 
                      src={profile.avatarUrl || '/default-avatar.png'} 
                      alt={profile.username} 
                    />
                  </div>
                  
                  <div className="profile-details">
                    <div className="profile-item">
                      <span className="profile-label">Nom d'utilisateur:</span>
                      <span className="profile-value">{profile.username}</span>
                    </div>
                    
                    <div className="profile-item">
                      <span className="profile-label">Email:</span>
                      <span className="profile-value">{profile.email}</span>
                    </div>
                    
                    <div className="profile-item">
                      <span className="profile-label">Bio:</span>
                      <span className="profile-value">{profile.bio || 'Aucune bio'}</span>
                    </div>
                    
                    <div className="profile-item">
                      <span className="profile-label">Membre depuis:</span>
                      <span className="profile-value">
                      {new Date(profile.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
            <CardFooter>
              {isEditing ? (
                <div className="profile-actions">
                  <Button 
                    type="outline" 
                    onClick={() => setIsEditing(false)}
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="primary" 
                    onClick={handleSaveProfile}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
                  </Button>
                </div>
              ) : (
                <Button 
                  type="primary" 
                  onClick={() => setIsEditing(true)}
                >
                  Modifier
                </Button>
              )}
            </CardFooter>
          </Card>

          {/* Deconnexion (le header ne l affiche plus depuis la r25) */}
          <div className="profile-logout" style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
            <Button type="outline" onClick={() => logout()}>
              Déconnexion
            </Button>
          </div>

          {/* R35 : date de naissance (18+) — les comptes existants peuvent la
              renseigner une seule fois (les duels à mise l exigent) */}
          {!user?.isGuest && (
            <Card>
              <CardHeader>
                <h2>Date de naissance {user?.adult ? '· 18+ ✓' : ''}</h2>
              </CardHeader>
              <CardContent>
                {birthDateSet ? (
                  <p style={{ margin: 0, color: 'var(--text-secondary, #999)' }}>
                    Enregistrée le jour de ta création de compte ou dans ton profil — non modifiable.
                  </p>
                ) : (
                  <div className="profile-form" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      type="date"
                      value={birthDateInput}
                      max={new Date().toISOString().slice(0, 10)}
                      onChange={(e) => setBirthDateInput(e.target.value)}
                    />
                    <Button type="primary" onClick={handleSetBirthDate} disabled={!birthDateInput}>
                      Enregistrer
                    </Button>
                  </div>
                )}
                {birthDateMsg && <p style={{ margin: '8px 0 0' }}>{birthDateMsg}</p>}
                <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text-secondary, #999)' }}>
                  Requise pour les duels à mise (18+). Stockée une seule fois, jamais modifiable.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
        
        <div className="profile-section">
          <Card>
            <CardHeader>
              <h2>Statistiques</h2>
            </CardHeader>
            <CardContent>
              {stats ? (
                <div className="stats-grid">
                  <div className="stat-item">
                    <span className="stat-value">{stats.wins}</span>
                    <span className="stat-label">Victoires</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{stats.losses}</span>
                    <span className="stat-label">Défaites</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">
                      {stats.winRatio ? `${stats.winRatio.toFixed(1)}%` : 'N/A'}
                    </span>
                    <span className="stat-label">Ratio</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{stats.cardsCollected}</span>
                    <span className="stat-label">Cartes</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{stats.decks}</span>
                    <span className="stat-label">Decks</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{stats.rank}</span>
                    <span className="stat-label">Rang</span>
                  </div>
                </div>
              ) : (
                <div className="no-stats">
                  <p>Aucune statistique disponible.</p>
                </div>
              )}
            </CardContent>
          </Card>
          
          <Card className="mt-3">
            <CardHeader>
              <h2>Préférences</h2>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <div className="preferences-form">
                  <div className="form-group">
                    <label htmlFor="preferences.theme">Thème</label>
                    <select 
                      id="preferences.theme" 
                      name="preferences.theme"
                      value={editedProfile.preferences?.theme || 'dark'}
                      onChange={handleInputChange}
                    >
                      <option value="dark">Sombre</option>
                      <option value="light">Clair</option>
                    </select>
                  </div>
                  
                  <div className="toggle-group">
                    <div className="toggle-item">
                      <label htmlFor="preferences.notifications">
                        <input
                          type="checkbox"
                          id="preferences.notifications"
                          name="preferences.notifications"
                          checked={editedProfile.preferences?.notifications}
                          onChange={handleToggleChange}
                        />
                        <span className="toggle-label">Notifications</span>
                      </label>
                    </div>
                    
                    <div className="toggle-item">
                      <label htmlFor="preferences.soundEffects">
                        <input
                          type="checkbox"
                          id="preferences.soundEffects"
                          name="preferences.soundEffects"
                          checked={editedProfile.preferences?.soundEffects}
                          onChange={handleToggleChange}
                        />
                        <span className="toggle-label">Effets sonores</span>
                      </label>
                    </div>
                    
                    <div className="toggle-item">
                      <label htmlFor="preferences.music">
                        <input
                          type="checkbox"
                          id="preferences.music"
                          name="preferences.music"
                          checked={editedProfile.preferences?.music}
                          onChange={handleToggleChange}
                        />
                        <span className="toggle-label">Musique</span>
                      </label>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="preferences-info">
                  <div className="preference-item">
                    <span className="preference-label">Thème:</span>
                    <span className="preference-value">
                      {profile.preferences?.theme === 'dark' ? 'Sombre' : 'Clair'}
                    </span>
                  </div>
                  
                  <div className="preference-item">
                    <span className="preference-label">Notifications:</span>
                    <span className="preference-value">
                      {profile.preferences?.notifications ? 'Activées' : 'Désactivées'}
                    </span>
                  </div>
                  
                  <div className="preference-item">
                    <span className="preference-label">Effets sonores:</span>
                    <span className="preference-value">
                      {profile.preferences?.soundEffects ? 'Activés' : 'Désactivés'}
                    </span>
                  </div>
                  
                  <div className="preference-item">
                    <span className="preference-label">Musique:</span>
                    <span className="preference-value">
                      {profile.preferences?.music ? 'Activée' : 'Désactivée'}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* R51 : compagnon + avatar générés via prompt */}
          <CompanionStudio />
        </div>
      </div>
    </div>
  );
};

export default Profile;